'use client'

import * as React from 'react'
import { Typography } from '@repo/design-system-cf/components/typography'
import { Breadcrumbs } from '@repo/web-ui/components/breadcrumbs'
import { SearchInput } from '@repo/design-system-cf/components/input'
import { useTranslations } from 'next-intl'
import { Link } from '@repo/web-ui/components/link'
import type { Locales } from 'cf-i18n/helpers'
import { addOrReplaceLocaleInUrl } from 'cf-i18n/helpers'
import Image from 'next/image'
import { Select } from '@repo/web-ui/components/select/select'
import { AppRoutes } from '@repo/web-utils/app-routes'

interface Category {
  name: string
  slug: string
  imageUrl?: string | null
}

export function Header({
  categories,
  locale,
  count,
  hideSearch = false,
  page,
}: {
  categories: Category[]
  locale: Locales
  count: number
  hideSearch?: boolean
  page?: number
}) {
  const t = useTranslations('pages.fonts.landing_page.header')
  const [category, setCategory] = React.useState('all')
  const [value, setValue] = React.useState('')

  const handleSearch = () => {
    const searchParams = new URLSearchParams()
    searchParams.set('query', value)
    searchParams.set('type', 'Fonts')
    if (category !== 'all') {
      searchParams.set('category', category)
    }
    window.location.href = `${AppRoutes.SEARCH}?${searchParams.toString()}`
  }

  const getCategoryName = (name: string) => {
    const fonts = t('fonts')
    if (!name.includes(fonts)) {
      return `${name} ${fonts}`
    }
    return name
  }

  return (
    <div className="flex flex-col">
      <Breadcrumbs
        items={[
          {
            title: t('breadcrumbs.home'),
            path: '/',
          },
          {
            title: t('breadcrumbs.fonts'),
            path: page ? AppRoutes.FONTS_RELATIVE_PATH : undefined,
          },
          ...(page
            ? [
                {
                  title: t('breadcrumbs.page', { number: page }),
                },
              ]
            : []),
        ]}
        separator="/"
      />
      <div className="flex flex-col items-center mx-auto mt-[20px]">
        <Typography variant="cf-title-08" asChild className="text-center">
          <h1>{t('title')}</h1>
        </Typography>
        <Typography variant="body-02" className="text-center mt-4 max-w-[664px]">
          {t('description', { count: count.toLocaleString() })}
        </Typography>
        <div className="hidden md:grid grid-cols-6 gap-x-4 gap-y-6 mt-10">
          {categories.map(category => (
            <Link
              className="flex flex-col justify-between bg-cf-white rounded shadow-sm group"
              key={category.slug}
              href={addOrReplaceLocaleInUrl(category.slug, locale)}
            >
              {category.imageUrl ? (
                <Image
                  className="aspect-[3/2] object-cover rounded-t"
                  src={category.imageUrl}
                  alt={category.name}
                  width={200}
                  height={200}
                />
              ) : (
                <div />
              )}
              <Typography variant="cf-title-01" asChild>
                <h3
                  className="px-2.5 py-1.5 group-hover:text-cf-theme-green-700"
                  dangerouslySetInnerHTML={{ __html: getCategoryName(category.name) }}
                />
              </Typography>
            </Link>
          ))}
        </div>
        {!hideSearch ? (
          <div
            id="search-section"
            className="flex shadow-md mt-10 md:mt-[88px] max-w-[800px] w-full"
          >
            <Select
              classNameTrigger="max-w-[140px] rounded-none h-[54px] border-none font-bold text-[#9093b2]"
              value={category}
              items={[
                {
                  value: 'all',
                  label: t('all'),
                },
                ...categories
                  .filter(category => !category.slug.includes('/tag/'))
                  .map(category => ({
                    value: category.name,
                    label: category.name,
                  })),
              ]}
              onValueChange={newCategory => setCategory(newCategory)}
            />
            <SearchInput
              className="bg-cf-white border-t-0 border-r-0 border-b-0 rounded-none"
              classNameInput="h-[54px] font-bold text-[#9093b2]"
              value={value}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue(e.target.value)}
              onSearch={handleSearch}
              placeholder={t('search_placeholder')}
              data-testid="fonts-search-input"
            />
          </div>
        ) : null}
      </div>
    </div>
  )
}
